import React, { lazy, Suspense, useEffect, useReducer, useState } from 'react'
import {
  StorageRounded, ReplyAllOutlined, AppRegistrationOutlined, EditLocationOutlined, ChevronRightOutlined
} from '@mui/icons-material'
import CircleIcon from '@mui/icons-material/Circle'
import { Box, CircularProgress, Typography, useTheme } from '@mui/material'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import { useNavigate } from 'react-router'
import { useLocation } from 'react-router-dom'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../generic/systemValues/systemValueLabel'
import { apiGet, apiPut } from '../generic/Api_Functions'
import { fabricationOrderName } from '../generic/utilities/fabricationOrderName'
import AlertUI from '../generic/AlertUI'
import SendProgram from '../configuration/devices/sendprogram/SendProgram'
import createDialogTitle from '../generic/utilities/dialogUtil'
import Document from '../configuration/document/Document'
import { useAuthorization } from '../generic/hooks/useAuthorization'
import { CompassButton } from '../Compass/Common/CompassButton'
import DeviceSetResource from '../configuration/devices/DeviceSetResource'
import StorageManager from '../management/StorageManager'
import FinishJobButton from '../configuration/parts/partHistory/FinishJobButton'
import PausePart from '../configuration/parts/pause/PausePart'
import './byGroup.css'

// Lazy-loaded: the calendar pulls in FullCalendar (heavy, ESM). Loading it only when the
// calendar modal opens keeps FullCalendar out of the static import graph of every card
// (and off the list-screen bundle) — same approach as MainImageThumbnail with the 3D viewer.
const EditCalendarEventsComponent = lazy(() => import('../configuration/calendars/EditCalendarEventsComponent'))

/**
 * @typedef {import('../generic/utilities/objectStructures/sendprogram/sendProgram').SendProgram} SendProgram SendProgram object
 * @typedef {import('../generic/utilities/objectStructures/sendprogram/sendProgram').SendProgramAction} SendProgramAction SendProgramAction object
 */

/**
 * AssetCardDialogs — dialogs / actions responsibility of the asset card (RN8-c).
 *
 * Owns every dialog the card can open (physical/planned parts, send program +
 * confirmation, move type, storage, document, location/resource-set) plus the actions
 * that trigger them (send program validation, move-resource pending-movement check,
 * manage location/storage, document, calendar navigation). Exposes the resulting
 * trigger functions to the presentation piece (`AssetCardView`) through a render-prop
 * (`children(actions)`) so the two pieces never need to know about each other's state.
 */
export default function AssetCardDialogs ({
  device,
  locations,
  loadStoragesByDevice,
  onPartsDialogOpenChange,
  onItemClick,
  onMainImageChange = () => {},
  children
}) {
  const [showMoveTypeDialog, setShowMoveTypeDialog] = useState(false)
  const [showSendProgramDialog, setShowSendProgramDialog] = useState(false)
  const [showConfirmSendProgramDialog, setShowConfirmSendProgramDialog] = useState(false)
  const [rowFirstResource, setRowFirstResource] = useState(null)
  const [resources, setResources] = useState([])
  const [dialogResourceSetOpen, setDialogResourceSetOpen] = useState(false)
  const [resourceSetDeviceName, setResourceSetDeviceName] = useState('')
  const [showStorageWindow, setShowStorageWindow] = useState(false)
  const [storageId, setStorageId] = useState(null)
  const [position, setPosition] = useState({ shelf: null, row: null, column: null })
  const [showDocument, setShowDocument] = useState(false)
  const [showCalendar, setShowCalendar] = useState(false)
  const [deviceRow, setDeviceRow] = useState(null)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  // Quick resource actions (story 35, PO): the physical-parts window is replaced by a
  // direct 3-action menu (Send program / Traceability / Move) per resource — opened
  // straight for a single resource, or after a picker when the device holds several.
  const [showResourcePicker, setShowResourcePicker] = useState(false)
  const [showResourceActions, setShowResourceActions] = useState(false)
  const [actionsRow, setActionsRow] = useState(null)
  // Full part behind the resource actions row, when the resource is a Part. Feeds the
  // reused traceability-header buttons (Finish job / Pause-Resume / Cancel part), which
  // need the complete part object (status, finished, jobRunning). {} when not a part.
  const [actionsPart, setActionsPart] = useState({})

  const { t_ } = useSmartTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [AlertElement, showAlert] = AlertUI()
  const theme = useTheme()
  const isUserAuthorized = useAuthorization()

  // D3 fix (RN19/CA7, S29/S30): the "move resource" flow used to hardcode
  // `urlBack: '/monitoring'`. Reading the actual current route instead makes the
  // return context correct for BOTH the untouched `/monitoring` screen (still no
  // query string there, so this evaluates to the exact same '/monitoring' as before —
  // zero behaviour change) AND the new by-cell screen at `/home`, which keeps the
  // selected cell/filter as query params (`?cell=&filter=`) precisely so this captures
  // them without either screen needing to know about the other.
  const urlBack = location.pathname + location.search

  // Keep the full part behind the resource actions dialog while it is open for a Part
  // resource, so the reused traceability-header buttons have the complete object. The id is
  // seeded synchronously (so the buttons always have a valid target from the first render)
  // and the full part is then polled every second — mirroring the traceability screen — so
  // status-driven labels (e.g. Pause ⇄ Resume) refresh in place without reopening the
  // dialog. Non-part resources (e.g. fixtures) clear it.
  useEffect(() => {
    const resource = actionsRow?.resources?.[0]
    if (!showResourceActions || resource?.type?.name !== 'Part' || resource?.id == null) {
      setActionsPart({})
      return
    }
    setActionsPart((prev) => (prev?.id === resource.id ? prev : { id: resource.id }))
    let active = true
    const load = () => {
      apiGet('parts/' + resource.id)
        .then((part) => {
          if (!active || !part) return
          // Keep the object identity stable when nothing changed, so children keyed on the
          // part prop (effects, memo) do not churn on every 1s tick.
          setActionsPart(prev => (JSON.stringify(prev) === JSON.stringify(part) ? prev : part))
        })
        .catch(() => {})
    }
    load()
    const interval = setInterval(load, 1000)
    return () => { active = false; clearInterval(interval) }
  }, [showResourceActions, actionsRow])

  // "Physical parts" → quick resource actions. Single resource: straight to the 3-action
  // menu. Several: the picker first. `onPartsDialogOpenChange` is preserved for callers
  // that pause other work while the parts flow is open.
  const openParts = () => {
    if (onPartsDialogOpenChange) onPartsDialogOpenChange(true)
    if (locations.length === 1) {
      setActionsRow(locations[0])
      setShowResourceActions(true)
    } else {
      setShowResourcePicker(true)
    }
  }
  const closeResourceFlow = () => {
    setShowResourcePicker(false)
    setShowResourceActions(false)
    if (onPartsDialogOpenChange) onPartsDialogOpenChange(false)
  }
  const selectResource = (row) => {
    setActionsRow(row)
    setShowResourcePicker(false)
    setShowResourceActions(true)
  }

  const getCellResource = async (row) => {
    const dates = await apiGet('commandMove/resourcesInsideCell')
    return dates.find((item) =>
      item.type === row.resources[0].type.name &&
      item.reference === row.resources[0].reference.code &&
      item.code === row.resources[0].code
    )
  }

  const getStorageResource = async (row) => {
    if (row.shelf === null) return null
    const dates = await apiGet('commandMove/' + row.deviceId + '/positions/false')
    return dates.find((item) =>
      parseInt(item.shelve) === parseInt(row.shelf) &&
      parseInt(item.row) === parseInt(row.row) &&
      parseInt(item.column) === parseInt(row.column))
  }

  // Per-resource action gating + handlers, shared by the quick-actions menu. `row` is a
  // physical location (its `resources[0]` is the resource/assembly on it).
  const traceabilityDisabled = (row) => !isUserAuthorized('PART_INSTANCE_HISTORY') || row?.resources?.[0]?.type?.name === 'Fixture'
  const sendProgramDisabled = (row) => !isUserAuthorized('PROGRAM_ACTION') ||
    device.status === 'WORKING' ||
    (device.deviceType.name !== 'CMM' && device.deviceType.name !== 'EDM' && device.deviceType.name !== 'MACHINING_CENTER' && device.deviceType.name !== 'PRINTER_3D') // system-value: raw-ok
  const moveDisabled = (row) => row?.position === null

  const handleTraceability = (row) => {
    onItemClick(row.resources[0])
    closeResourceFlow()
  }

  const handleSendProgram = (row) => {
    setResources(row.resources)
    setShowResourceActions(false)
    setShowSendProgramDialog(true)
  }

  const handleMove = async (row) => {
    setRowFirstResource(row.resources[0])
    setPosition({ shelf: row.shelf, row: row.row, column: row.column })
    const cellResource = await getCellResource(row)
    const storageResource = await getStorageResource(row)
    if (!cellResource || (row.shelf !== null && !storageResource)) {
      showAlert({
        title: t_('Error: '),
        message: t_('The resource already has a pending movement.'),
        severity: 'error'
      })
      return
    }
    setShowResourceActions(false)
    setShowMoveTypeDialog(true)
  }

  /** Human location for a physical row: the device itself, or its storage position. */
  const locationLabel = (row) => {
    if (!row || row.shelf === null || row.shelf === undefined) return device.name
    return 'Shelf: ' + row.shelf + ' - Row: ' + row.row + ' - Column: ' + row.column
  }

  const onDispatchData = (state, action) => {
    const newState = { ...state }
    switch (action.type) {
      case 'SET_SEND_PRESET':
        newState.sendPreset = action.send
        return newState
      case 'SET_SEND_PROGRAM':
        newState.sendProgram = action.send
        return newState
      case 'SET_PROGRAM_LIST':
        newState.programList = action.currentProgramList
        return newState
      case 'SET_PRESET':
        newState.preset = action.currentPreset
        return newState
      case 'RESET_VALUES':
        newState.sendPreset = true
        newState.sendProgram = true
        newState.programList = []
        newState.preset = null
        return newState
      default:
        return newState
    }
  }

  /** @type {[SendProgram, React.Dispatch<SendProgramAction>]} */
  const [sendProgramData, sendProgramDispatchdata] = useReducer(onDispatchData, {
    sendPreset: true,
    sendProgram: true,
    preset: null,
    programList: [],
    device
  })

  const [sendProgramAlert, setSendProgramAlert] = useState(null)

  const validateSendResources = () => {
    if (!sendProgramData.sendPreset && !sendProgramData.sendProgram) {
      setSendProgramAlert({ title: '', message: t_('You must select at least one option to send.'), severity: 'info' })
      return
    }
    if (sendProgramData.sendPreset && (sendProgramData.preset === null)) {
      setSendProgramAlert({ message: t_('You must select a preset.'), severity: 'warning' })
      return
    }
    if (sendProgramData.sendProgram && sendProgramData.programList.length === 0) {
      setSendProgramAlert({ message: t_('You must select at least one program.'), severity: 'warning' })
      return
    }
    setShowConfirmSendProgramDialog(true)
  }

  const getCircleWithColor = (row) => {
    let color
    switch (row.status) {
      case 'EXECUTING':
        color = theme.palette.background.green
        break
      case 'EXECUTABLE':
        color = theme.palette.background.blue
        break
      case 'LOCKED':
        color = theme.palette.background.red
        break
      default:
        color = theme.palette.background.grey
        break
    }
    return <CircleIcon sx={{ color, fontSize: 18 }} />
  }

  const sendResources = async () => {
    const data = { deviceId: device.id, programList: sendProgramData.programList, preset: sendProgramData.preset }
    await apiPut('device/sendProgram', data).then(() => {
      showAlert({ title: t_('Success: '), message: t_('Sending files completed successfully.'), severity: 'success' })
      sendProgramDispatchdata({ type: 'RESET_VALUES' })
      setShowSendProgramDialog(false)
      setSendProgramAlert(null)
    }).catch((error) => {
      setSendProgramAlert({ title: t_('Error sending files'), message: error.message, severity: error.name })
    })
  }

  const manageLocation = async () => {
    if (device.deviceType.name === 'STORAGE') { // system-value: raw-ok
      const storage = await loadStoragesByDevice()
      setStorageId(storage.id)
      setShowStorageWindow(true)
      return
    }
    setDialogResourceSetOpen(true)
    setResourceSetDeviceName(device.name)
  }

  const actions = {
    openParts,
    manageLocation,
    openDocument: () => {
      setDeviceRow(device)
      setShowDocument(true)
    },
    goToCalendar: () => {
      // PO (2026-07-10): open the calendar in a modal instead of navigating away, so the
      // monitoring context (cell/filter/detail) is never lost.
      setShowCalendar(true)
    },
    isUserAuthorized
  }

  return (
    <>
      {/* Pick a resource (only when the device holds several physical resources) */}
      <SmartDialog
        setOpen={showResourcePicker}
        close
        accept={false}
        cancel={false}
        closeCallback={closeResourceFlow}
        title={{ icon: <StorageRounded color='primary' />, render: <Typography variant='h5' color='primary' sx={{ marginLeft: '0.3em' }}>{device.name}</Typography> }}
        renderComponent={
          <div style={{ minWidth: 460, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {AlertElement}
            <Typography variant='subtitle2' color='text.secondary' sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t_('Select a resource')}</Typography>
            {locations.length === 0 && <Typography color='text.secondary'>{t_('No physical resources')}</Typography>}
            {locations.map((row, index) => (
              <Box
                key={index}
                component='button'
                type='button'
                data-testid={`resource-pick-${index}`}
                onClick={() => selectResource(row)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  width: '100%',
                  textAlign: 'left',
                  p: 1.2,
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: 'divider',
                  background: 'transparent',
                  color: 'text.primary',
                  '&:hover': { borderColor: 'primary.main' }
                }}
              >
                {getCircleWithColor(row)}
                <span style={{ fontSize: 15, fontWeight: 600 }}>{row.resources?.[0]?.code}</span>
                <span style={{ fontSize: 13, opacity: 0.85 }}>{systemValueLabel(t_, 'ResourceTypeEnum', row.resources?.[0]?.type?.name || '')}</span>
                {fabricationOrderName(row.resources?.[0]) && (
                  <span data-testid={`resource-pick-fabrication-order-${index}`} style={{ fontSize: 13, opacity: 0.85 }}>
                    {t_('Fabrication order')}: {fabricationOrderName(row.resources?.[0])}
                  </span>
                )}
                <span style={{ marginLeft: 'auto', fontSize: 13, opacity: 0.85 }}>{locationLabel(row)}</span>
                <ChevronRightOutlined />
              </Box>
            ))}
          </div>
        }
      />
      {/* The 3 quick actions for the selected resource (Send program / Traceability / Move) */}
      <SmartDialog
        setOpen={showResourceActions}
        close
        accept={false}
        cancel={false}
        closeCallback={closeResourceFlow}
        title={{ icon: <StorageRounded color='primary' />, render: <Typography variant='h5' color='primary' sx={{ marginLeft: '0.3em' }}>{actionsRow?.resources?.[0]?.code ?? device.name}</Typography> }}
        renderComponent={
          <div style={{ minWidth: 460, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {AlertElement}
            {actionsRow && (
              <Typography color='text.secondary' variant='body2'>
                {systemValueLabel(t_, 'ResourceTypeEnum', actionsRow.resources?.[0]?.type?.name || '')} · {locationLabel(actionsRow)}
              </Typography>
            )}
            {/* Only parts carry an order; for anything else this renders nothing, not an empty label. */}
            {fabricationOrderName(actionsRow?.resources?.[0]) && (
              <Typography color='text.secondary' variant='body2' data-testid='resource-action-fabrication-order'>
                {t_('Fabrication order')}: {fabricationOrderName(actionsRow?.resources?.[0])}
              </Typography>
            )}
            {/* Square touch buttons, same style as the machine cards (mcx-touch-btn) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
              <button
                type='button'
                className='mcx-touch-btn mcx-touch-btn--lg'
                data-testid='resource-action-send'
                disabled={!actionsRow || sendProgramDisabled(actionsRow)}
                onClick={() => handleSendProgram(actionsRow)}
              >
                <ReplyAllOutlined sx={{ fontSize: 32 }} />
                <span className='mcx-touch-btn__label'>{t_('Send program')}</span>
              </button>
              <button
                type='button'
                className='mcx-touch-btn mcx-touch-btn--lg'
                data-testid='resource-action-traceability'
                disabled={!actionsRow || traceabilityDisabled(actionsRow)}
                onClick={() => handleTraceability(actionsRow)}
              >
                <AppRegistrationOutlined sx={{ fontSize: 32 }} />
                <span className='mcx-touch-btn__label'>{t_('Traceability')}</span>
              </button>
              <button
                type='button'
                className='mcx-touch-btn mcx-touch-btn--lg'
                data-testid='resource-action-move'
                disabled={!actionsRow || moveDisabled(actionsRow)}
                onClick={() => handleMove(actionsRow)}
              >
                <EditLocationOutlined sx={{ fontSize: 32 }} />
                <span className='mcx-touch-btn__label'>{t_('Move')}</span>
              </button>
              {/* Part-level actions reused from the traceability header (story #2745): the
                  same Finish job / Pause-Resume / Cancel part buttons rendered as touch
                  buttons so they match the grid, letting the operator act on the part
                  without first entering its traceability. "Next job" is left out on purpose
                  (not functional yet; reworked in another story). Only shown when the
                  resource is a part and its full object has loaded. */}
              {actionsRow?.resources?.[0]?.type?.name === 'Part' && (
                <>
                  <FinishJobButton part={actionsPart} buttonVariant='touch' />
                  <PausePart part={actionsPart} showAlert={showAlert} hideNextJob buttonVariant='touch' />
                </>
              )}
            </div>
          </div>
        }
      />
      <SmartDialog
        title={{ icon: <ReplyAllOutlined color='primary' />, render: <Typography color='primary' variant='h5'>{t_('Send')}</Typography> }}
        setOpen={showSendProgramDialog}
        renderComponent={
          <SendProgram
            device={device}
            resource={resources.length > 0 ? resources[0] : null}
            fixture={resources.length > 0 ? resources[resources.length - 1] : null}
            data={sendProgramData}
            dispatchdata={sendProgramDispatchdata}
            alert={sendProgramAlert}
          />
        }
        acceptCallback={() => validateSendResources()}
        cancelCallback={() => {
          sendProgramDispatchdata({ type: 'RESET_VALUES' })
          setSendProgramAlert(null)
          setShowSendProgramDialog(false)
        }}
      />
      <SmartDialog
        title={{ icon: <StorageRounded color='primary' />, render: <Typography color='primary' variant='h5'>{t_('Storage')}</Typography> }}
        setOpen={showStorageWindow}
        renderComponent={showStorageWindow ? <StorageManager initialStorageId={storageId} /> : null}
        accept={false}
        cancel={false}
        close
        closeCallback={() => setShowStorageWindow(false)}
        paperWidth='95vw'
        growContainerHeight
      />
      <SmartDialog
        message={t_('Are you sure you want to send selected programs?')}
        title={createDialogTitle({ color: 'success', title: t_('Send programs?'), icon: ReplyAllOutlined })}
        acceptCallback={() => { setShowConfirmSendProgramDialog(false); sendResources() }}
        cancelCallback={() => setShowConfirmSendProgramDialog(false)}
        setOpen={showConfirmSendProgramDialog}
      />
      <SmartDialog
        title={{ icon: <EditLocationOutlined fontSize='large' color='primary' />, render: <Typography color='primary' variant='h5'>{t_('Move type')}</Typography> }}
        setOpen={showMoveTypeDialog}
        renderComponent={
          <div>
            <CompassButton
              label={t_('MOVE BY LOCATION')}
              width='auto'
              height='auto'
              labelStyle={{ fontSize: '1.8rem' }}
              onClick={() => {
                navigate('/process/command/byLocation', { state: { position, device, urlBack } })
              }}
              sx={{ padding: '1.5rem' }}
            />
            <CompassButton
              label={t_('MOVE BY RESOURCE')}
              width='auto'
              height='auto'
              labelStyle={{ fontSize: '1.8rem' }}
              onClick={() => {
                navigate('/process/command/byResource', { state: { rowFirstResource, device, urlBack } })
              }}
              sx={{ padding: '1.5rem', marginLeft: '10px' }}
            />
          </div>
        }
        close={false}
        accept={false}
        cancel
        cancelCallback={() => setShowMoveTypeDialog(false)}
      />
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        renderComponent={<Document reference={deviceRow} resourceType='DEVICE' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} onMainImageChange={onMainImageChange} />}
        setOpen={showDocument}
      />
      <SmartDialog
        title={t_('Calendar') + ' - ' + device.name}
        closeCallback={() => setShowCalendar(false)}
        accept={false}
        cancel={false}
        close
        minWidth='80vw'
        paperHeight='85vh'
        growContainerHeight
        renderComponent={
          <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}>
            {/* The calendar's action buttons use `position: fixed` (anchored to the
                viewport on its full-page route). Inside a modal that lets them escape and
                cover the dialog's Close. A `transform` here creates a containing block so
                those fixed buttons resolve against THIS box and stay within the modal.
                The calendar's own nested dialogs render through a portal, so they are not
                descendants and remain unaffected. */}
            <Box sx={{ position: 'relative', transform: 'translateZ(0)', width: '100%', height: '100%', overflow: 'auto' }}>
              <EditCalendarEventsComponent isReference={false} calendarId={device.calendarId} />
            </Box>
          </Suspense>
        }
        setOpen={showCalendar}
      />
      {/* Mounted on demand: DeviceSetResource fetches `devicesBasicData/noStorage` in a
          mount effect regardless of `open`, so mounting it unconditionally made EVERY
          card fire that request at screen load (N+1 across the whole grid). */}
      {dialogResourceSetOpen && (
        <DeviceSetResource
          open={dialogResourceSetOpen}
          deviceName={resourceSetDeviceName}
          setDeviceName={setResourceSetDeviceName}
          setOpen={setDialogResourceSetOpen}
        />
      )}
      {children(actions)}
    </>
  )
}
